import React, {useRef, useCallback, useState, useEffect} from 'react';
import styled, {keyframes, css} from 'styled-components';
import {AccentColorOptions, useBreakpointDown} from 'epic-ue-theme';
import Vibrant from 'node-vibrant';
import {InView} from 'react-intersection-observer';
import {setSize, setOffset, getGradient, setGradient} from '../../../utils/';
import {ImageProps} from '../../elements';
import {getOptImage, useBrowserSatisfies} from 'epic-ue-shared';
import classNames from 'classnames';

export const CIRCLE = 'circular';
export const OVAL = 'oval';
export const SIZESMALL = 'small';
export const SIZEMEDIUM = 'medium';
export const SIZEBIG = 'big';
export const TILTLEFT = 'left';
export const TILTRIGHT = 'right';
const BLURAMOUNT = 200;
const OPACITY = 0.6;

export type SpotlightShapeOptions = typeof OVAL | typeof CIRCLE;
export type SpotlightVariantOptions = typeof SIZESMALL | typeof SIZEMEDIUM | typeof SIZEBIG;
export type SpotlightTiltOptions = typeof TILTLEFT | typeof TILTRIGHT;
export type SpotlightVibrantOptions = {
    from: string;
    to: string;
};

export interface SpotlightProps {
    shape?: SpotlightShapeOptions;
    accentColor?: AccentColorOptions;
    variant?: SpotlightVariantOptions;
    tilt?: SpotlightTiltOptions;
    children: any;
    pulsate?: boolean;
    disable?: boolean;
    showImage?: boolean;
    opacity?: number;
    image?: string | ImageProps;
    width?: number;
    height?: number;
    offsetRatio?: number[]; // [x,y]
    offsetRatioDown?: number[]; // [x,y]
    customColors?: string[]; // [x,y]
    optMaxWidth?: number;
    zIndex?: number;
    defaultInView?: boolean;
    invertHorizontalRatio?: boolean;
    invertVerticalRatio?: boolean;
}

const SpotlightWrapper = styled.div`
    position: relative;
    width: 100%;
    z-index: 0;
`;

const animateGlow = keyframes`
    0%{
        filter: blur(${BLURAMOUNT}px);
    }
    50%{
        filter: blur(${BLURAMOUNT + 20}px);
    }
    100%{
        filter: blur(${BLURAMOUNT}px);
    }
`;

const SpotlightGradient = styled.div<{
    accentGradient: SpotlightVibrantOptions;
    shape: SpotlightShapeOptions;
    variant: SpotlightVariantOptions;
    tilt?: string;
    pulsate?: boolean;
    hasLoaded: boolean;
    showImage?: boolean;
    image?: string | ImageProps;
    opacity?: number;
    offsetRatio: number[]; // [x,y]
    offsetRatioDown: number[]; // [x,y]
    innerHeight: number;
    innerWidth: number;
    width?: number;
    height?: number;
    vibrantColor: SpotlightVibrantOptions;
    zIndex?: number;
    invertHorizontalRatio?: boolean;
    invertVerticalRatio?: boolean;
}>`
    position: absolute;
    ${({invertVerticalRatio}) => (invertVerticalRatio ? 'bottom' : 'top')}: ${({
        innerHeight,
        offsetRatio
    }) => setOffset(innerHeight, offsetRatio, 'y')};
    ${({invertHorizontalRatio}) => (invertHorizontalRatio ? 'right' : 'left')}: ${({
        innerWidth,
        offsetRatio
    }) => setOffset(innerWidth, offsetRatio, 'x')};
    ${(p) => p.theme.breakpoints.down('sm')} {
        top: ${({innerHeight, offsetRatioDown}) => setOffset(innerHeight, offsetRatioDown, 'y')};
        left: ${({innerWidth, offsetRatioDown}) => setOffset(innerWidth, offsetRatioDown, 'x')};
    }
    opacity: ${(p) => (p.hasLoaded ? p.opacity : '0')};
    z-index: ${(p) => (p.zIndex ? `${p.zIndex}` : '-1')};
    pointer-events: none;
    mix-blend-mode: normal;
    width: calc(
        ${({variant, shape, width, height}) => setSize(variant, shape, 'width', width, height)} -
            ${(p) => (p.showImage ? '0' : BLURAMOUNT)}px
    );
    height: calc(
        ${({variant, shape, width, height}) => setSize(variant, shape, 'height', width, height)} -
            ${(p) => (p.showImage ? '0' : BLURAMOUNT)}px
    );
    ${(p) => p.theme.breakpoints.down('sm')} {
        width: calc(
            ${({variant, shape, width, height}) => setSize(variant, shape, 'width', width, height)} -
                ${(p) => (p.showImage ? '0' : '50')}px
        );
        height: calc(
            ${({variant, shape, width, height}) => setSize(variant, shape, 'height', width, height)} -
                ${(p) => (p.showImage ? '0' : '50')}px
        );
    }
    margin: 0 auto;
    border-radius: ${(p) => (p.showImage ? '0' : '9999px')};
    transform: ${(p) =>
        p.shape === OVAL && p.tilt !== ''
            ? `translate3d(0,0,0) rotate(${p.tilt === TILTLEFT ? '-30' : '30'}deg)`
            : 'translate3d(0,0,0)'};
    filter: ${(p) => (p.showImage ? 'none' : `blur(${BLURAMOUNT}px)`)};
    background: ${({accentGradient, vibrantColor, image, showImage}) =>
        setGradient(accentGradient, vibrantColor, image, showImage)};
    background-size: 100% 100%;
    background-size: cover;
    background-repeat: no-repeat;

    animation: ${(p) =>
        p.pulsate
            ? css`
                  ${animateGlow} 3s ease infinite
              `
            : ''};

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }

    &.hidden {
        filter: initial;
        background: initial;
        animation: none;
        opacity: initial;
        width: initial;
        height: initial;
    }
`;

/**
 *
 *
 * @param shape: Can be either 'circular' or 'oval'.
 * @param accentColor:  Accepts the accent color we use all over the place like sky or fire.
 * @param variant:  Either small, medium or big. The oval shape only comes in small and big.
 * @param tilt: The oval can tilt either left or right.
 * @param showImage: Outright show the image provide rather then using a provided image as a color reference.
 * @param image:  This string is used by Vibrant to get the dominant colors.
 * @param offsetRatio: Override the offset manually, based on a x/y , 0 to 1 ratio.
 * @param offsetRatioDown: Same as above but for small screens.
 * @param customColors: An array with two hex strings.
 * @param pulsate: Animate the glow to pulsate a bit.
 * @param opacity: Override the default opacity. The opacity defaults to 1 if an image is shown otherwise it defaults to 0.35.
 * @param width: Override the default width.
 * @param height: Override the default height.
 * @param disable: Allow for the spotlight to remain in a component, but not render the actual spotlight.
 *
 *
 */

export const Spotlight = (props: SpotlightProps): JSX.Element => {
    const spotlightGradient = useRef<HTMLDivElement>(null);
    const browserDisabledSpotlight = useBrowserSatisfies({
        safari: '<14',
        'internet explorer': '>8',
        chrome: '<70',
        firefox: '<70'
    });
    const {
        shape = CIRCLE,
        accentColor = '',
        variant = SIZEMEDIUM,
        tilt = '',
        width,
        height,
        offsetRatio = [],
        offsetRatioDown = [],
        pulsate = false,
        disable = false,
        showImage = false,
        customColors,
        opacity = showImage ? 1 : OPACITY,
        image = '',
        children,
        optMaxWidth,
        zIndex,
        defaultInView = false,
        invertHorizontalRatio = false,
        invertVerticalRatio = false
    } = props;
    const disableSpotlight = disable || browserDisabledSpotlight;
    const [vibrantColor, setVibrantColor] = useState<SpotlightVibrantOptions>({
        from: '',
        to: ''
    });

    const isSm = useBreakpointDown('sm');
    const [inView, setInView] = useState(defaultInView);
    const [innerHeight, setInnerHeight] = useState();
    const [innerWidth, setInnerWidth] = useState();
    const [hasLoaded, setHasLoaded] = useState(false);
    const [accentGradient, setAccentGradient] = useState<SpotlightVibrantOptions>({
        from: '',
        to: ''
    });

    const visChange = useCallback(
        (visible) => {
            if (!inView) {
                setInView(visible);
            }
        },
        [inView, setInView]
    );

    // Use Callback to get up to date ref info
    const spotlightInnerRef = useCallback(
        (node) => {
            if (node !== null && hasLoaded) {
                const rect = node.getBoundingClientRect();
                if (innerHeight !== rect.height && rect.height > 50) {
                    setInnerHeight(rect.height);
                }
                if (innerWidth !== rect.width && rect.width > 50) {
                    setInnerWidth(rect.width);
                }
            }
        },
        [setInnerHeight, setInnerWidth, hasLoaded, innerHeight, innerWidth]
    );

    useEffect(() => {
        if (!hasLoaded) {
            setHasLoaded(true);
        }
    }, [hasLoaded, setHasLoaded]);

    useEffect(() => {
        if (disableSpotlight || !inView) return;
        if (image && !customColors && !showImage) {
            let sourceImage;

            if (typeof image === 'string') {
                sourceImage = image;
            } else if (image.src) {
                sourceImage = image.src;
            }
            let width = optMaxWidth || 600;
            //reduce the image size to make the analysis faster
            if (isSm) {
                width *= 0.05;
            } else {
                width *= 0.2;
            }
            sourceImage = getOptImage({url: sourceImage, width});
            if (typeof sourceImage === 'string') {
                Vibrant.from(sourceImage).getPalette((err, palette) => {
                    if (palette && !err) {
                        setVibrantColor({
                            from: palette.Vibrant ? palette.Vibrant.getHex() : '',
                            to: palette.LightVibrant ? palette.LightVibrant.getHex() : ''
                        });
                    }
                });
            }
        } else if (
            customColors &&
            customColors.length > 0 &&
            customColors[0] !== '' &&
            customColors[1] !== ''
        ) {
            setVibrantColor({
                from: customColors[0],
                to: customColors[1]
            });
        } else if (accentColor && !image) {
            const spotlightAccentGradient = getGradient(accentColor);
            // Make sure to reset the vibrant colors for dynamic items
            setVibrantColor({
                from: '',
                to: ''
            });
            setAccentGradient({
                from: spotlightAccentGradient.from,
                to: spotlightAccentGradient.to
            });
        } else {
            setVibrantColor({
                from: '',
                to: ''
            });
        }
    }, [
        setVibrantColor,
        image,
        customColors,
        showImage,
        accentColor,
        setAccentGradient,
        inView,
        disableSpotlight,
        optMaxWidth,
        isSm
    ]);

    if (disableSpotlight) {
        return <div className="ue-spotlight-wrap">{children}</div>;
    }

    const hidden = disableSpotlight || !inView;


    return (
        <InView as="div" onChange={visChange} threshold={0}>
            <SpotlightWrapper className="spotlight-wrapper">
                <SpotlightGradient
                    className={classNames('spotlight', {hidden})}
                    shape={shape}
                    hasLoaded={hasLoaded}
                    image={image}
                    showImage={showImage}
                    accentGradient={accentGradient}
                    variant={variant}
                    pulsate={pulsate}
                    vibrantColor={vibrantColor || {}}
                    tilt={tilt}
                    width={width}
                    height={height}
                    opacity={opacity}
                    ref={spotlightGradient}
                    offsetRatio={offsetRatio}
                    offsetRatioDown={offsetRatioDown}
                    innerHeight={innerHeight || 0}
                    innerWidth={innerWidth || 0}
                    zIndex={zIndex}
                    invertHorizontalRatio={invertHorizontalRatio}
                    invertVerticalRatio={invertVerticalRatio}
                />
                <div ref={spotlightInnerRef}>
                    {React.Children.map(props.children, child => {
                        return child;
                    })}
                </div>
            </SpotlightWrapper>
        </InView>
    );
};
